<template>
  <div
    v-click-outside="onContextMenuClose"
    class="c-list-item flex items-center whitespace-nowrap overflow-hidden overflow-ellipsis"
    :class="{ 'c-list-item--selected': selected || draggedOver }"
    :draggable="!!selectedIds.length && movable"
    @click="handleClick"
    @contextmenu="onContextMenuOpen"
    @dragend="handleDragEnd"
    @dragenter.prevent
    @dragleave="handleDragLeave"
    @dragover="handleDragOver"
    @dragstart="handleDragStart"
    @drop="handleDrop"
  >
    <slot
      :index="index"
      :isContextMenuOpen="isContextMenuOpen"
      :isDraggedOver="draggedOver"
      :isSelected="selectedIds?.includes?.(item.id)"
      :item="item"
    />
    <NPopover v-if="isContextMenuOpen" raw show :showArrow="false" trigger="manual" :x="contextMenuX" :y="contextMenuY">
      <slot :item="item" name="item:context-menu" />
    </NPopover>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { NPopover } from 'naive-ui'

import ClickOutsideDirective from '@lib/directives/clickOutside'
import useThemeVars from '@lib/composables/useThemeVars'

interface ListItemProps {
  isContextMenuOpen: boolean
  index: number
  item: { id: number | string; [key: string]: any }
  selectedIds: (number | string)[]
  contextMenuX?: number
  contextMenuY?: number
  movable?: boolean
}

const props = withDefaults(defineProps<ListItemProps>(), {
  contextMenuX: 0,
  contextMenuY: 0,
  isContextMenuOpen: false,
  movable: false,
})
const emit = defineEmits<{
  click: [item: ListItemProps['item'], event: MouseEvent]
  dragging: [isDragging: boolean]
  dragOver: [event: DragEvent]
  move: [move: { targetItemId: ListItemProps['item']['id']; itemIds: ListItemProps['item']['id'][] }]
  contextMenu: [item: ListItemProps['item'], isOpen: boolean, e?: MouseEvent]
}>()
const vClickOutside = ClickOutsideDirective
const draggedOver = ref(false)
const themeVars = useThemeVars()

const onContextMenuOpen = (e: MouseEvent) => {
  e.preventDefault()
  emit('contextMenu', props.item, true, e)
}
const onContextMenuClose = () => {
  emit('contextMenu', props.item, false)
}

const selected = computed(() => props.selectedIds.includes(props.item.id))

const dragData = ref<{ itemIds: ListItemProps['item']['id'][]; ghostElem: HTMLElement | null }>({
  itemIds: [],
  ghostElem: null,
})

function handleClick(event: MouseEvent) {
  if (props.isContextMenuOpen) {
    onContextMenuClose()
    return
  }
  emit('click', props.item, event)
}

function handleDragStart(event: DragEvent) {
  if (!props.movable) {
    event.preventDefault()
    return
  }

  if (!selected.value) {
    handleClick(event)
  }

  event.dataTransfer?.setData('text/plain', JSON.stringify(props.selectedIds))

  const ghostElem = document.createElement('div')
  ghostElem.style.backgroundColor = themeVars.value.primary
  ghostElem.style.color = themeVars.value.base
  ghostElem.className = 'pa-3 w-12 h-6 flex justify-center items-center text-xs font-bold rounded-2'
  ghostElem.textContent = `${props.selectedIds.length}`

  document.body.appendChild(ghostElem)

  event.dataTransfer!.setDragImage(ghostElem, 0, 0)
  dragData.value = { itemIds: props.selectedIds, ghostElem }
  emit('dragging', true)
  setTimeout(() => {
    document.body.removeChild(ghostElem)
  }, 0)
}

function handleDragEnd() {
  dragData.value = { itemIds: [], ghostElem: null }
  emit('dragging', false)
}

function handleDragOver(event: DragEvent) {
  event.preventDefault()
  event.dataTransfer?.setData('text/plain', JSON.stringify(props.selectedIds))

  emit('dragOver', event)
  if (draggedOver.value) return

  draggedOver.value = true
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault()
  draggedOver.value = false
}

function handleDrop(event: DragEvent) {
  event.preventDefault()
  const itemIds = JSON.parse(event.dataTransfer?.getData('text/plain') || '[]')
  emit('move', { targetItemId: props.item.id, itemIds })
  draggedOver.value = false
}
</script>
